import {
  getCountByStatus,
  useGetAllAudiovisuals,
} from "@/api/audiovisual/audiovisual";
import NewAudiovisualDialog from "@/components/audiovisual/NewAudiovisualDialog";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import {
  audiovisualAdvancedSearchConfig,
  audiovisualColumns,
} from "@/config/dashboard/audiovisual";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Audiovisual } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "audiovisual-table-preferences";

const flatVisibleAudiovisualColumns = audiovisualColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const AudiovisualDashboard = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    currentCount: totalCount,
    countByStatus,
    pagination,
    setPagination,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Audiovisual>({
    resourceName: "audiovisual",
    resourceCounter: getCountByStatus,
    statusField: "audiovisuelPublicationStatus",
  });

  const { data } = useGetAllAudiovisuals(fetchParams);

  const columns: ColumnDef<Audiovisual, any>[] = useMemo(() => {
    return generateTable(audiovisualColumns, "audiovisual");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleAudiovisualColumns;
    }
    return flatVisibleAudiovisualColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...audiovisualColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...audiovisualColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">
          {t("dashboardTitleAudiovisuels")}
        </p>
        <GlobalSearch
          config={audiovisualAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          onValueChange={setFilterStatus}
          countByStatus={countByStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={audiovisualAdvancedSearchConfig}
            resource="audiovisuals"
          />
          <AdvancedSort config={audiovisualAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createAaudiovisual")}
            Dialog={NewAudiovisualDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => navigate(`/edit/audiovisual/${row.original.id}`)}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: totalCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
          }}
        />
      </div>
    </div>
  );
};

export default AudiovisualDashboard;
